// Relatórios nativos (por curso e por aluno), em tempo real a partir do Firestore.
// A aba do Looker Studio (painel externo) só aparece para empresas com inquilinos.lookerStudioUrl.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../config/firebaseConfig';
import Carregando from '../components/Carregando';
import RelatorioAluno from '../components/relatorios/RelatorioAluno';
import RelatorioCurso from '../components/relatorios/RelatorioCurso';
import { useBaseRelatorio } from '../components/relatorios/useRelatorios';
import { telefoneDoProgresso } from '../components/relatorios/util';
import '../components/relatorios/Relatorios.css';

const ABAS = [
  { id: 'curso', rotulo: 'Por curso' },
  { id: 'aluno', rotulo: 'Por aluno' },
  { id: 'looker', rotulo: 'Looker Studio' },
];

function Relatorios() {
  const tenantId = localStorage.getItem('tenantId');
  const [params, setParams] = useSearchParams();
  const [lookerUrl, setLookerUrl] = useState(null);
  const abas = ABAS.filter((a) => a.id !== 'looker' || lookerUrl);
  const aba = abas.some((a) => a.id === params.get('aba')) ? params.get('aba') : 'curso';

  useEffect(() => {
    if (!tenantId) return;
    getDoc(doc(firestore, 'inquilinos', tenantId))
      .then((d) => setLookerUrl((d.exists() && d.data().lookerStudioUrl) || null))
      .catch(() => setLookerUrl(null));
  }, [tenantId]);
  const { minicursos, progressos, nomesPorTelefone, erro, atualizadoEm } = useBaseRelatorio(tenantId);

  const nomeDe = useCallback(
    (p) => p.nome || nomesPorTelefone[telefoneDoProgresso(p)] || null,
    [nomesPorTelefone],
  );

  // Ativos primeiro, depois os com mais alunos
  const cursos = useMemo(() => {
    if (!minicursos || !progressos) return [];
    const inscritos = {};
    progressos.forEach((p) => { inscritos[p.minicursoId] = (inscritos[p.minicursoId] || 0) + 1; });
    return minicursos
      .map((m) => ({ ...m, inscritos: inscritos[m.id] || 0 }))
      .sort((a, b) => (b.status === 'ativo') - (a.status === 'ativo') || b.inscritos - a.inscritos
        || String(a.nome).localeCompare(String(b.nome)));
  }, [minicursos, progressos]);

  const curso = cursos.find((c) => c.id === params.get('curso')) || cursos[0];

  const navegar = (mudancas) => {
    const proximo = new URLSearchParams(params);
    Object.entries(mudancas).forEach(([chave, valor]) => (valor ? proximo.set(chave, valor) : proximo.delete(chave)));
    setParams(proximo);
  };

  const carregando = !minicursos || !progressos;

  return (
    <div className="qz-pagina">
      <div className="rel-pagina">
        <header className="rel-cabecalho">
          <h1 className="qz-pagina__titulo rel-titulo">Relatórios</h1>
          {atualizadoEm && (
            <p className="rel-subtitulo">
              <span className="rel-ao-vivo" aria-hidden="true" />
              Atualiza sozinho a cada resposta recebida · última atualização às {atualizadoEm.toLocaleTimeString('pt-BR')}
            </p>
          )}
        </header>

        <div className="rel-abas" role="tablist" aria-label="Tipo de relatório">
          {abas.map((a) => (
            <button
              key={a.id}
              type="button"
              role="tab"
              id={`rel-aba-${a.id}`}
              aria-selected={aba === a.id}
              aria-controls="rel-painel"
              className={`rel-aba${aba === a.id ? ' is-ativa' : ''}`}
              onClick={() => navegar({ aba: a.id })}
            >
              {a.rotulo}
            </button>
          ))}
        </div>

        <div id="rel-painel" role="tabpanel" aria-labelledby={`rel-aba-${aba}`}>
          {!tenantId && <p className="qz-empty">Não foi possível identificar a empresa. Saia e entre de novo.</p>}
          {tenantId && erro && <p className="qz-empty">{erro}</p>}

          {tenantId && !erro && aba === 'looker' && (
            <div className="rel-looker">
              <p className="rel-secao__apoio">
                Painel externo do Looker Studio: só abre para contas Google com acesso a ele.
              </p>
              <iframe
                title="Relatório do Looker Studio"
                src={`${lookerUrl}?params=${encodeURIComponent(JSON.stringify({ 'ds18.tenantId': tenantId }))}`}
                allowFullScreen
                sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              />
            </div>
          )}

          {tenantId && !erro && aba !== 'looker' && carregando && <Carregando texto="Carregando relatórios..." />}

          {tenantId && !erro && aba === 'curso' && !carregando && (
            cursos.length === 0 ? (
              <p className="qz-empty">Nenhum minicurso cadastrado. Crie um em Configurações › Minicursos.</p>
            ) : (
              <>
                <div className="rel-filtros">
                  <label htmlFor="rel-curso">Curso</label>
                  <select
                    id="rel-curso"
                    className="rel-select"
                    value={curso.id}
                    onChange={(e) => navegar({ curso: e.target.value })}
                  >
                    {cursos.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome} · {c.inscritos} {c.inscritos === 1 ? 'aluno' : 'alunos'}{c.status === 'ativo' ? '' : ' (inativo)'}
                      </option>
                    ))}
                  </select>
                </div>
                <RelatorioCurso
                  key={curso.id}
                  tenantId={tenantId}
                  curso={curso}
                  progressos={progressos}
                  nomeDe={nomeDe}
                  onVerAluno={(telefone) => navegar({ aba: 'aluno', aluno: telefone })}
                />
              </>
            )
          )}

          {tenantId && !erro && aba === 'aluno' && !carregando && (
            <RelatorioAluno
              tenantId={tenantId}
              minicursos={minicursos}
              progressos={progressos}
              nomeDe={nomeDe}
              telefone={params.get('aluno')}
              onSelecionar={(telefone) => navegar({ aluno: telefone })}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Relatorios;
